import {
  AutoComplete,
  Checkbox,
  Chip,
  ChipContainer,
  Flex,
  Icon,
  IconButtonButton,
  IconButtonInnerChild,
  Table,
  JSONEditorContainer,
} from "@evergis/ui";

import styled, { css } from "styled-components";

import { EqlParameter, EqlParameterContainer, ParameterDivider } from "../../../../modules/Configuration/styled";
import { StyledMap } from "../../../../modules/Map/styled";
import { MessageTitle } from "../../Import/styled";

export const AttributesIndexContainer = styled(Flex)<{ disabled?: boolean }>`
  width: 100%;
  padding-top: 5px;
  padding-bottom: 5px;
  align-items: center;
  ${({ disabled }) =>
    disabled
      ? css`
          opacity: 0.28;
        `
      : ""}
`;

export const AttributeCheckbox = styled(Checkbox)`
  path:first-child {
    fill: rgba(0, 0, 0, 0.28);
  }
`;

export const AttributesIndexType = styled(Flex)`
  width: 9rem;
  margin-left: auto;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
`;

export const AttributesIndexTypeIcon = styled.div<{ icon: string }>`
  width: 16px;
  height: 16px;
  opacity: 0.28;
  background: url("${({ icon }) => icon}") center center no-repeat;
  margin-right: 0.5rem;
`;

export const TableContainer = styled.div<{ isVisible: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;
  ${({ isVisible }) =>
    !isVisible &&
    css`
      display: none;
    `}
`;

export const StyledTable = styled(Table)`
  tr {
    background-color: ${({ theme }) => theme.palette.background};
  }
`;

export const EmptyTableDataContainer = styled.div`
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

export const EmptyTableDataIcon = styled(Icon)`
  height: 2rem;
  width: 2rem;
  line-height: 1rem;
  opacity: 0.12;
  &:after {
    font-size: 2rem;
  }
`;

export const EmptyTableDataTitle = styled.div`
  font-size: 0.75rem;
  color: ${({ theme: { palette } }) => palette.textSecondary};
  margin-top: 0.75rem;
  text-align: center;
`;

export const PreviewMapContainer = styled(StyledMap)<{ isVisible: boolean }>`
  ${({ isVisible }) =>
    !isVisible &&
    css`
      display: none;
    `}
`;

export const EQLEditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const EqlTableContainer = styled(EqlParameterContainer)<{ isAction?: boolean }>`
  ${ChipContainer} {
    background-color: rgba(0, 170, 255, 0.06);
    color: rgba(0, 170, 255);

    ${IconButtonButton} {
      ${({ isAction }) =>
        isAction
          ? css`
              height: 1.25rem;
              padding: 0.25rem 0.5rem;
              border-radius: ${({ theme: { borderRadius } }) => borderRadius.smallest};
            `
          : css`
              height: 0.75rem;
              padding-left: 0.25rem;
              padding-right: 0.25rem;
            `}

      &:active {
        background-color: rgba(0, 132, 214, 0.28) !important;
      }

      &:hover {
        background-color: rgba(0, 149, 230, 0.28);
      }

      ${IconButtonInnerChild} {
        color: rgba(0, 170, 255);
      }

      span[kind] {
        color: rgba(0, 170, 255);
      }
    }
  }
`;

export const EqlTableItem = styled(EqlParameter)`
  button {
    color: rgba(0, 170, 255);
  }
`;

export const ErrorMessageWrapper = styled(EmptyTableDataContainer)`
  height: 100%;
  background-color: rgba(255, 0, 0, 0.06);
  overflow-y: scroll;
`;

export const ErrorMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  margin: auto;
  padding: 1rem;

  span[kind] {
    color: rgb(255, 0, 0);
    width: 1.5rem;
    height: 1.5rem;

    :after {
      font-size: 1.5rem;
    }
  }

  ${MessageTitle} {
    color: rgb(255, 0, 0);
    text-transform: uppercase;
    font-size: 0.75rem;
  }
`;

export const TableDivider = styled(ParameterDivider)`
  border-color: rgba(0, 170, 255);
`;

TableDivider.defaultProps = {
  vertical: true,
};

export const ParameterChip = styled(Chip)`
  height: 1.25rem;
  padding: 0;

  ${IconButtonButton} {
    height: 1.25rem !important;
  }
`;

export const StyledAutocomplete = styled(AutoComplete)`
  input {
    width: 12rem !important;
  }
`;

export const QueryTablesContainer = styled.div`
  display: flex;
  flex-shrink: 0;
`;

export const QueryParametersContainer = styled.div`
  display: flex;
  flex-shrink: 0;
`;

export const QueryEditorContainer = styled(JSONEditorContainer)`
  margin-top: 1rem;
  box-sizing: border-box;
  border: 1px solid rgba(0, 0, 0, 0.12);
  flex-shrink: unset;

  .jsoneditor,
  .ace-sqlserver {
    border: thin solid ${({ theme: { palette } }) => palette.elementDeep};
  }

  .ace_marker-layer {
    .ace_active-line,
    .ace_selection {
      background-color: ${({ theme: { palette } }) => palette.elementDark};
    }
  }

  .ace_keyword,
  .ace_variable {
    color: ${({ theme: { palette } }) => palette.primary};
  }

  .ace_string {
    color: ${({ theme: { palette } }) => palette.secondary};
  }

  .ace_invalid_constant {
    color: ${({ theme: { palette } }) => palette.error};
  }

  .ace_line,
  .ace_identifier,
  .ace_constant.ace_numeric {
    color: ${({ theme: { palette } }) => palette.textPrimary};
  }

  .ace_gutter,
  .ace_gutter-active-line {
    background: transparent;
  }
`;
